import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { nanoid } from 'nanoid'

import { ConvertImageOptions, convertImage as convertImageFunc } from '@/utils/image'
import { upload as uploadFunc, UploadUrlResult } from '@/utils/storage'
import { ToastProgressRef, toast } from '@/components/Toaster'
import { showAlertDialog } from '@/components/new/ui'

type UploadHandlerOptions = {
    convertImage?: ConvertImageOptions
    maxFileSize?: number
    hideProgress?: boolean
    getUploadUrl?: () => Promise<UploadUrlResult>
}

export type UploadHandler = (
    blob: Blob,
    options?: UploadHandlerOptions
) => Promise<{ url: string; fileId: string; fileName?: string; file: File } | null>

export const useUpload = () => {
    const { t } = useTranslation()

    const objectUrls = useRef<string[]>([])
    const [pendingUploadCount, setPendingUploadCount] = useState<number>(0)
    const toastProgressRefs = useRef<Record<string, ToastProgressRef | undefined>>({})
    const uploadFnRef = useRef<UploadHandler>()

    useEffect(() => {
        const cleanupUrls = objectUrls.current

        return () => {
            cleanupUrls.map((url) => URL.revokeObjectURL(url))
        }
    }, [])

    const clearToastProgress = useCallback(
        (uploadId: string) => {
            const toastId = toastProgressRefs.current[uploadId]?.toastId
            if (toastId) {
                toast.remove(toastId)
                toastProgressRefs.current[uploadId] = undefined
            }
        },
        [toastProgressRefs]
    )

    const upload = useCallback<UploadHandler>(
        async (blob, { convertImage, maxFileSize = 0, hideProgress, getUploadUrl } = {}) => {
            setPendingUploadCount((value) => value + 1)

            const uploadId = nanoid()
            let renderProgress = false
            let lastProgress = 0

            const progressHandler = (progress: number) => {
                lastProgress = progress
                if (renderProgress && !hideProgress) {
                    const toastProgressRef = toastProgressRefs.current[uploadId]

                    if (toastProgressRef) {
                        toastProgressRef.setProgress(progress)
                    } else {
                        if (progress !== 100) {
                            toastProgressRefs.current[uploadId] = toast.createProgress((progress) =>
                                t('components.useUpload.progress', { progress })
                            )
                        }
                    }
                }
            }

            // we only start to render the progress after 1.5s
            const progressTimeout = setTimeout(() => {
                renderProgress = true
                // first render with the last registered progress
                progressHandler(lastProgress)
            }, 1500)

            try {
                const fileName = blob instanceof File ? (blob as File).name : undefined

                if (typeof blob.size === 'number' && maxFileSize > 0) {
                    if (blob.size > maxFileSize) {
                        showAlertDialog({
                            severity: 'warning',
                            title: t('components.useUpload.fileTooLarge.title'),
                            message: t('components.useUpload.fileTooLarge.message'),
                        })

                        return null
                    }
                }

                if (convertImage && ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(blob.type)) {
                    ;({ blob } = await convertImageFunc(blob, convertImage))
                }

                try {
                    const fileId = await uploadFunc(blob, {
                        onProgress: progressHandler,
                        getUploadUrl,
                    })

                    const objectUrl = URL.createObjectURL(blob)
                    objectUrls.current.push(objectUrl)

                    return {
                        url: objectUrl,
                        fileId,
                        fileName,
                        file: new File([blob], fileName || '', { type: blob.type }),
                    }
                } catch (error) {
                    // TODO: capture sentry error
                    showAlertDialog({
                        severity: 'error',
                        title: t('components.useUpload.uploadError.title'),
                        message: t('components.useUpload.uploadError.message'),
                    })
                }
            } finally {
                // clear the progress + progress timeout
                clearTimeout(progressTimeout)
                clearToastProgress(uploadId)

                setPendingUploadCount((value) => value - 1)
            }

            return null
        },
        [clearToastProgress, t, toastProgressRefs]
    )

    useEffect(() => {
        uploadFnRef.current = upload
    }, [upload])

    return { upload, isUploading: !!pendingUploadCount, uploadFnRef }
}
